import { useMemo, useState, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, User, FileText, Target, ExternalLink } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAppointment } from "@/hooks/use-api";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { EmptyState } from "@/components/v2/empty-state";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { StatusBadge } from "@/components/status-badge";
import { formatDate, formatDateOnly } from "@/lib/utils";

type Row = Record<string, unknown>;

const TAB_TRIGGER_CLASS =
  "rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent";

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function formatSchedule(
  date?: string | null,
  time?: string | null,
): string | null {
  if (!date && !time) return null;
  if (date && time) return `${formatDateOnly(date)} · ${time}`;
  if (date) return formatDateOnly(date);
  return time ?? null;
}

export default function V2AppointmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const { data, isLoading, isError } = useAppointment(id ?? "");
  const appointment = (data as { appointment?: Row } | undefined)?.appointment;

  const status = (appointment?.status as string | null) ?? "scheduled";
  const customerName = (appointment?.customer_name as string | null) ?? null;
  const repName = (appointment?.assigned_rep_name as string | null) ?? null;
  const opportunityName =
    (appointment?.opportunity_name as string | null) ?? null;
  const customerRoleId =
    (appointment?.customer_role_id as string | null) ?? null;
  const opportunityId = (appointment?.opportunity_id as string | null) ?? null;
  const proposalId = (appointment?.proposal_id as string | null) ?? null;
  const apptType = (appointment?.appointment_type as string | null) ?? null;

  const schedule = useMemo(
    () =>
      formatSchedule(
        appointment?.appointment_date as string | null,
        appointment?.appointment_time as string | null,
      ),
    [appointment],
  );

  const title = apptType
    ? `${apptType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} appointment`
    : "Appointment";

  const subhead = useMemo(() => {
    if (schedule && customerName) return `${schedule} · ${customerName}`;
    return schedule ?? customerName ?? null;
  }, [schedule, customerName]);

  const hasLinkedRecords = Boolean(
    customerRoleId || opportunityId || proposalId,
  );

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        role="status"
        aria-label="Loading appointment"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (isError || (!isLoading && !appointment)) {
    const errTitle = isError
      ? "Failed to load appointment"
      : "Appointment not found";
    const errDescription = isError
      ? "Something went wrong. Please try again."
      : "This appointment may have been deleted.";
    return (
      <>
        <Breadcrumb className="mb-4 typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/appointments"
                className="text-[color:var(--color-text-lo)]"
              >
                Appointments
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{id ?? "..."}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <EmptyState title={errTitle} description={errDescription} />
      </>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Breadcrumbs + per-page actions cluster */}
      <div className="mb-3 flex items-center justify-between">
        <Breadcrumb className="typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/appointments"
                className="text-[color:var(--color-text-lo)]"
              >
                Appointments
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2">
          <DetailPageActions entityLabel="Appointment" />
        </div>
      </div>

      {/* 3-tab shell (Overview · Activity · Records) — Pattern A */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col"
      >
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0 mb-0">
          <TabsTrigger value="overview" className={TAB_TRIGGER_CLASS}>
            Overview
          </TabsTrigger>
          <TabsTrigger value="activity" className={TAB_TRIGGER_CLASS}>
            Activity
          </TabsTrigger>
          <TabsTrigger value="records" className={TAB_TRIGGER_CLASS}>
            Records
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-0">
          <div className="flex gap-6 pt-6">
            {/* Main column */}
            <div className="flex-1 min-w-0 space-y-5">
              <div>
                <h1 className="typo-heading text-[color:var(--color-text-hi)]">
                  {title}
                </h1>
                {subhead && (
                  <p className="mt-0.5 typo-body text-[color:var(--color-text-md)]">
                    {subhead}
                  </p>
                )}
              </div>

              {/* Inline properties strip */}
              <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
                <StatusBadge status={status} />
                {schedule && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>{schedule}</span>
                  </>
                )}
                {appointment?.timezone != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>{appointment.timezone as string}</span>
                  </>
                )}
                {appointment?.created_at != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>
                      Created {formatDate(appointment.created_at as string)}
                    </span>
                  </>
                )}
              </div>

              {/* Notes */}
              {(appointment?.notes != null ||
                appointment?.scheduler_notes != null) && (
                <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4 space-y-3">
                  {appointment?.notes != null && (
                    <div>
                      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-1">
                        Notes
                      </div>
                      <p className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
                        {appointment.notes as string}
                      </p>
                    </div>
                  )}
                  {appointment?.scheduler_notes != null && (
                    <div>
                      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-1">
                        Scheduler notes
                      </div>
                      <p className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
                        {appointment.scheduler_notes as string}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Activity timeline — full feed at bottom of main column */}
              <RailSection title="Activity">
                <EntityActivityFeed
                  entityType="appointment"
                  entityId={id!}
                  emptyDescription="Messages, calls, and notes for this appointment appear here once the first contact is logged."
                />
              </RailSection>
            </div>

            {/* Right rail */}
            <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
              <RailSection title="Properties">
                <InfoRow label="Status" value={status} />
                <InfoRow label="Type" value={apptType} />
                <InfoRow label="Scheduled" value={schedule} />
                <InfoRow
                  label="Timezone"
                  value={(appointment?.timezone as string | null) ?? null}
                />
                <InfoRow label="Customer" value={customerName} />
                <InfoRow label="Rep" value={repName} />
                <InfoRow label="Opportunity" value={opportunityName} />
                <InfoRow
                  label="Created"
                  value={
                    appointment?.created_at
                      ? formatDate(appointment.created_at as string)
                      : null
                  }
                />
              </RailSection>

              <RailSection title="Activity">
                <EntityActivityPreview
                  entityType="appointment"
                  entityId={id!}
                  onSeeAll={() => setActiveTab("activity")}
                />
              </RailSection>
            </div>
          </div>
        </TabsContent>

        {/* ── Activity ── */}
        <TabsContent value="activity" className="mt-0 pt-6">
          <EntityActivityFeed
            entityType="appointment"
            entityId={id!}
            emptyDescription="Messages, calls, and notes for this appointment appear here once the first contact is logged."
          />
        </TabsContent>

        {/* ── Records ── */}
        <TabsContent value="records" className="mt-0 pt-6 space-y-6">
          {hasLinkedRecords ? (
            <div>
              <h2 className="typo-body font-semibold text-[color:var(--color-text-hi)] mb-3">
                Linked records
              </h2>
              <div className="flex flex-col gap-2">
                {customerRoleId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/customers/${customerRoleId}`)}
                    className="justify-start gap-2"
                  >
                    <User className="h-4 w-4" />
                    Customer · {customerName ?? "View customer"}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
                {opportunityId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/opportunities/${opportunityId}`)}
                    className="justify-start gap-2"
                  >
                    <Target className="h-4 w-4" />
                    Opportunity · {opportunityName ?? "View opportunity"}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
                {proposalId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/proposals/${proposalId}`)}
                    className="justify-start gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Proposal · View proposal
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <EmptyState
              title="No linked records"
              description="Link this appointment to a customer, opportunity, or proposal to see related records here."
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
